/**
 * @file asset-service.ts
 * A client-side service for interacting with the Asset Management API.
 */
import { v4 as uuidv4 } from 'uuid';

// --- TYPE DEFINITIONS ---
export type AssetStatus = 'active' | 'archived';

export interface Asset {
  uuid: string;
  name: string;
  type: string;
  status: AssetStatus;
  createdAt: string;
  updatedAt: string | null;
  version: number;
  data: unknown;
}

export interface AssetListItem extends Omit<Asset, 'data'> {}

export interface AssetType {
    name: string;
    label: string;
    description?: string | null;
}

export interface AssetVersionRecord {
  id: number;
  version: number;
  status: 'active' | 'archived';
  createdAt: string | number | Date | null;
  updatedAt: string | number | Date | null;
  data: unknown;
  isCurrent: boolean;
}

// --- SHARING TYPE DEFINITIONS ---
export interface AssetPermission {
  role: 'owner' | 'editor' | 'viewer';
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canShare: boolean;
}

export interface AssetShareInfo {
  id: number;
  targetType: 'user' | 'organization' | 'team' | 'public';
  targetId: string;
  targetName: string;  // Human-readable name resolved via SQL join (name, email, or ID)
  role: 'owner' | 'editor' | 'viewer';
  grantedBy: string;
  createdAt: string;
}

export interface AssetSharingStatus {
  isPublic: boolean;
  hasUserShares: boolean;
  hasOrgShares: boolean;
  hasTeamShares: boolean;
}

export interface AssetWithPermission extends Asset {
  permission?: AssetPermission;
  sharedWith?: AssetShareInfo[];
}

export interface MetadataItem {
  id: number;
  assetUuid: string;
  key: string;
  value: string;
}

export interface PaginatedAssetsResponse {
    data: AssetListItem[];
    pagination: {
        page: number;
        limit: number;
        totalItems: number;
        totalPages: number;
    };
}

// --- CONFIGURATION ---
const API_BASE_URL = '/api';

// --- PRIVATE HELPERS ---

/**
 * Creates and returns authenticated headers for API requests.
 * Note: Better Auth handles authentication automatically via cookies.
 */
function getAuthHeaders(contentType: 'json' | 'none' = 'json'): Headers {
    const headers = new Headers();
    // Better Auth uses HTTP-only cookies for authentication, so no manual token needed
    if (contentType === 'json') {
        headers.append('Content-Type', 'application/json');
    }
    // Include credentials to send cookies with requests
    return headers;
}

/**
 * A generic response handler for fetch calls.
 */
async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
        const errorMessage = errorData.error || `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
    }
    return response.json();
}

// --- PUBLIC ASSET SERVICE ---
export const assetService = {
    /**
     * Retrieves a paginated list of assets. (Public)
     * Now accepts a single type or an array of types for category filtering.
     * Can optionally include public assets in the results.
     */
    listAssets: async (params: { type?: string | string[]; page?: number; limit?: number; includePublic?: boolean } = {}): Promise<PaginatedAssetsResponse> => {
        const queryParams = new URLSearchParams();

        if (params.type) {
            // If type is an array, join it into a comma-separated string for the API
            const typeValue = Array.isArray(params.type) ? params.type.join(',') : params.type;
            queryParams.set('type', typeValue);
        }

        if (params.page) queryParams.set('page', String(params.page));
        if (params.limit) queryParams.set('limit', String(params.limit));
        if (params.includePublic) queryParams.set('includePublic', 'true');

        const requestUrl = `${API_BASE_URL}/assets?${queryParams.toString()}`;
        const response = await fetch(requestUrl, {
            credentials: 'include'
        });
        return handleResponse<PaginatedAssetsResponse>(response);
    },

    /**
     * Retrieves a paginated list of assets shared with a specific organization. (Public)
     * Now accepts a single type or an array of types for category filtering.
     * This function fetches assets that are specifically shared with the given organization.
     */
    listOrganizationAssets: async (orgId: string, params: { type?: string | string[]; page?: number; limit?: number } = {}): Promise<PaginatedAssetsResponse> => {
        const queryParams = new URLSearchParams();
        
        if (params.type) {
            // If type is an array, join it into a comma-separated string for the API
            const typeValue = Array.isArray(params.type) ? params.type.join(',') : params.type;
            queryParams.set('type', typeValue);
        }

        if (params.page) queryParams.set('page', String(params.page));
        if (params.limit) queryParams.set('limit', String(params.limit));
        
        // Use the existing organization-specific assets endpoint
        const requestUrl = `${API_BASE_URL}/assets/organization/${orgId}?${queryParams.toString()}`;
        const response = await fetch(requestUrl, {
            credentials: 'include'
        });
        return handleResponse<PaginatedAssetsResponse>(response);
    },

    /**
     * Retrieves a list of all available asset types. (Public)
     */
    listAssetTypes: async (): Promise<AssetType[]> => {
        const response = await fetch(`${API_BASE_URL}/types`, {
            credentials: 'include'
        });
        return handleResponse<AssetType[]>(response);
    },

    /**
     * Retrieves a single asset by its UUID. (Public)
     */
    getAsset: async (uuid: string, resolve: boolean = false): Promise<Asset> => {
        const url = `${API_BASE_URL}/assets/${uuid}${resolve ? '?resolve=true' : ''}`;
        const response = await fetch(url, {
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    /**
     * PREVIEW HELPER: Retrieves the file URI for an asset by its UUID.
     *
     * Use this in preview components that need just the URI (e.g., image/video src).
     * Supports both new (URI in asset.data) and legacy patterns.
     *
     * @param uuid The asset UUID
     * @returns The file URI, or null if not available
     */
    async getAssetFileUri(uuid: string): Promise<string | null> {
        try {
            const asset = await assetService.getAsset(uuid);

            if (!asset.data || typeof asset.data !== 'object') {
                return null;
            }

            const dataObj = asset.data as Record<string, unknown>;

            // Check for URI field
            if ('uri' in dataObj && typeof dataObj.uri === 'string') {
                const uri = dataObj.uri;
                // Validate URI format
                if (uri.startsWith('/d/') || uri.startsWith('http')) {
                    return uri;
                }
                console.warn('Invalid URI format from API:', uri);
                return null;
            }

            return null;
        } catch (error) {
            console.error('Error fetching asset URI:', error);
            return null;
        }
    },

    /**
     * Retrieves the content of an asset by its UUID, with special handling for URI-based content.
     * For assets with content stored at URIs (determined by asset type), this fetches the actual content.
     * For other assets, this returns the data field directly.
     */
    async getAssetContent(uuid: string): Promise<unknown> {
        const asset = await assetService.getAsset(uuid);
        
        // Import the configuration function to check if this asset type stores content at URI
        // We'll use the logic from content-type-config.ts
        const isContentAtUriType = (type: string): boolean => {
            const contentAtUriTypes = [
                'application/json',
                'text/plain', 
                'text/markdown',
                'text/csv',
                'application/scene+json',
                'model/gltf+json',
                'application/animator-controller+json',
                'application/agent.persona+json',
                'application/agent.voice+json',
                'application/game-settings+json',
                'application/item-database+json',
            ];
            return contentAtUriTypes.includes(type);
        };
        
        // Check if asset data has a URI and the type indicates content is stored there
        if (asset.data && typeof asset.data === 'object' && 'uri' in asset.data && isContentAtUriType(asset.type)) {
            const uri = asset.data.uri as string;
            let dataUrl = uri;
            
            // Construct the proper URL based on the URI format
            if (uri.startsWith('/d/')) {
                dataUrl = `${window.location.origin}${uri}`;
            } else if (!uri.startsWith('http')) {
                dataUrl = `${window.location.origin}/${uri}`;
            }
            
            // Fetch the actual content from the URI
            const response = await fetch(dataUrl);
            if (!response.ok) {
                throw new Error(`Failed to load asset content from ${dataUrl}: ${response.statusText}`);
            }
            
            const content = await response.text();
            try {
                // Try to parse as JSON if possible
                return JSON.parse(content);
            } catch {
                // If not valid JSON, return as text
                return content;
            }
        } else {
            // For non-URI-based assets, return the data field directly
            return asset.data;
        }
    },

    /**
     * Creates a new asset. Handles both file-based and data-only creation. (Protected)
     */
    createAsset: async (assetData: Omit<Asset, 'status' | 'createdAt' | 'updatedAt'>, file?: File): Promise<Asset> => {
        if (file) {
            const headers = getAuthHeaders('none');
            const formData = new FormData();
            formData.append('file', file);
            formData.append('uuid', assetData.uuid);
            formData.append('name', assetData.name);
            formData.append('type', assetData.type);
            formData.append('version', String(assetData.version));
            
            const response = await fetch(`${API_BASE_URL}/assets`, {
                method: 'POST',
                headers,
                body: formData,
                credentials: 'include'
            });
            return handleResponse<Asset>(response);
        } else {
            const headers = getAuthHeaders('json');
            const response = await fetch(`${API_BASE_URL}/assets`, {
                method: 'POST',
                headers,
                body: JSON.stringify(assetData),
                credentials: 'include'
            });
            return handleResponse<Asset>(response);
        }
    },
    
    /**
     * NEW: Generic helper to create any asset from a file. (Protected)
     * This replaces all the specific create helpers like createVrmAsset.
     * @param file The file to upload.
     * @param name The desired name for the new asset.
     * @param type The full asset type string (e.g., 'model/fbx').
     */
    createFileAsset: async (file: File, name: string, type: string): Promise<Asset> => {
        const payload = {
            uuid: uuidv4(),
            name,
            type, // The type is now a parameter
            version: 1,
            data: {}, 
        };
        return assetService.createAsset(payload, file);
    },

    /**
     * NEW: Create asset with dependencies. (Protected)
     * Creates a main asset file with associated dependency files.
     * @param mainFile The main file to upload
     * @param name The desired name for the new asset
     * @param type The asset type string
     * @param dependencies Map of dependency filenames to File objects
     */
    createAssetWithDependencies: async (
        mainFile: File,
        name: string,
        type: string,
        dependencies: Record<string, File>
    ): Promise<Asset> => {
        const payload = {
            uuid: uuidv4(),
            name,
            type,
            version: 1,
            data: {}, // This will be populated by the backend with URI and dependencies
        };

        const headers = getAuthHeaders('none');
        const formData = new FormData();

        // Append main file
        formData.append('file', mainFile);
        formData.append('uuid', payload.uuid);
        formData.append('name', payload.name);
        formData.append('type', payload.type);
        formData.append('version', String(payload.version));

        // Append dependencies with specific names to distinguish them
        Object.entries(dependencies).forEach(([filename, file]) => {
            // Use 'dependencies' as the field name and include the original filename as the third parameter
            formData.append('dependencies', file, filename);
        });

        const response = await fetch(`${API_BASE_URL}/assets`, {
            method: 'POST',
            headers,
            body: formData,
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    /**
     * NEW: Create asset from text/config content. (Protected)
     * Uploads text or JSON content to R2 and creates an asset with URI reference.
     * @param content The text or JSON content to store
     * @param name The desired name for the new asset
     * @param type The asset type string (e.g., 'text/plain', 'application/json')
     * @param filename The filename for the content file in R2 (with extension)
     */
    createAssetFromContent: async (
        content: string,
        name: string,
        type: string,
        filename: string = 'content.txt'
    ): Promise<Asset> => {
        // Convert string content to a File blob
        const blob = new Blob([content], { type: 'text/plain' });
        const file = new File([blob], filename, { type: 'text/plain' });

        // Use createFileAsset to upload the content file
        // This will store the file in R2 and return asset with { uri: "/d/..." }
        const asset = await assetService.createFileAsset(file, name, type);
        return asset;
    },

    /**
     * Updates an existing asset's name or data. (Protected)
     */
    updateAsset: async (uuid: string, updateData: { name?: string; data?: unknown }): Promise<Asset> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(updateData),
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    /**
     * Updates an existing asset's file content. This creates a new version with updated file content. (Protected)
     * @param uuid The UUID of the asset to update.
     * @param file The new file content to upload.
     * @param name The name for the asset.
     * @param type The type of the asset.
     */
    updateAssetFileContent: async (uuid: string, file: File, name: string, type: string): Promise<Asset> => {
        return assetService.replaceAssetMainFile(uuid, file, { name, type });
    },

    /**
     * Updates asset content from text/config string. (Protected)
     * Converts the string content to a file, uploads to R2, and creates new version.
     * Used for editing text and config assets.
     * @param uuid The UUID of the asset to update
     * @param content The text or JSON content to store
     * @param filename The filename for the content in R2
     * @param type The asset type
     */
    updateAssetContent: async (uuid: string, content: string, filename: string = 'content.txt', type?: string): Promise<Asset> => {
        // Convert string content to a File blob
        const blob = new Blob([content], { type: 'text/plain' });
        const file = new File([blob], filename, { type: 'text/plain' });

        // Use replaceAssetMainFile to upload the content and create new version
        return assetService.replaceAssetMainFile(uuid, file, { type });
    },

    replaceAssetMainFile: async (
        uuid: string,
        file: File,
        options: { removeDependencies?: boolean; name?: string; type?: string } = {}
    ): Promise<Asset> => {
        const formData = new FormData();
        formData.append('file', file);
        if (options.name) {
            formData.append('name', options.name);
        }
        if (options.type) {
            formData.append('type', options.type);
        }
        if (options.removeDependencies) {
            formData.append('removeDependencies', 'true');
        }

        const response = await fetch(`${API_BASE_URL}/assets/${uuid}`, {
            method: 'PUT',
            body: formData,
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    /**
     * Lists all versions for an asset.
     */
    listAssetVersions: async (uuid: string): Promise<AssetVersionRecord[]> => {
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/versions`, {
            credentials: 'include'
        });
        const data = await handleResponse<{ versions: AssetVersionRecord[] }>(response);
        return data.versions;
    },

    listAssetMetadata: async (uuid: string): Promise<MetadataItem[]> => {
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/meta`, {
            credentials: 'include'
        });
        return handleResponse<MetadataItem[]>(response);
    },

    uploadDependencies: async (
        uuid: string,
        dependencies: Array<{ key: string; file: File }>
    ): Promise<Asset> => {
        if (!dependencies.length) {
            throw new Error('At least one dependency is required.');
        }

        const formData = new FormData();
        dependencies.forEach(({ key, file }) => {
            formData.append('dependencyKeys', key);
            formData.append('dependencies', file, file.name);
        });

        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/dependencies`, {
            method: 'POST',
            body: formData,
            credentials: 'include'
        });

        return handleResponse<Asset>(response);
    },

    renameDependency: async (uuid: string, key: string, newKey: string): Promise<Asset> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/dependencies/${encodeURIComponent(key)}`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify({ newKey }),
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    removeDependency: async (uuid: string, key: string): Promise<Asset> => {
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/dependencies/${encodeURIComponent(key)}`, {
            method: 'DELETE',
            credentials: 'include'
        });
        return handleResponse<Asset>(response);
    },

    /**
     * Archives an asset, marking it as inactive. (Protected)
     */
    archiveAsset: async (uuid: string): Promise<{ message: string }> => {
        const headers = getAuthHeaders('none');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}`, { 
            method: 'DELETE',
            headers,
            credentials: 'include'
        });
        return handleResponse<{ message: string }>(response);
    },

    /**
     * Archives multiple assets in a single request. (Protected)
     */
    bulkArchiveAssets: async (uuids: string[]): Promise<{ message: string }> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets`, {
            method: 'DELETE',
            headers,
            body: JSON.stringify({ uuids }),
            credentials: 'include'
        });
        return handleResponse<{ message: string }>(response);
    },

    /**
     * Get asset sharing relationships (detailed - for sharing panel)
     */
    getAssetSharing: async (uuid: string): Promise<AssetShareInfo[]> => {
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/sharing`, {
            credentials: 'include'
        });
        return handleResponse<AssetShareInfo[]>(response);
    },

    /**
     * Get asset sharing status (lightweight - for asset list)
     * Returns boolean flags instead of full details - much more efficient for lists
     */
    getAssetSharingStatus: async (uuid: string): Promise<AssetSharingStatus> => {
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/sharing/status`, {
            credentials: 'include'
        });
        return handleResponse<AssetSharingStatus>(response);
    },

    /**
     * Share asset with user/organization/public
     */
    shareAsset: async (
        uuid: string, 
        targetType: 'user' | 'organization' | 'public', 
        targetId: string, 
        role: 'owner' | 'editor' | 'viewer'
    ): Promise<{message: string}> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/share`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ targetType, targetId, role }),
            credentials: 'include'
        });
        return handleResponse<{message: string}>(response);
    },

    /**
     * Remove asset share
     */
    removeAssetShare: async (
        uuid: string,
        targetType: 'user' | 'organization' | 'team' | 'public',
        targetId: string
    ): Promise<{message: string}> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/share?targetType=${encodeURIComponent(targetType)}&targetId=${encodeURIComponent(targetId)}`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
        });
        return handleResponse<{message: string}>(response);
    },

    /**
     * Remove public sharing access from an asset (make it un-public)
     */
    makeAssetUnPublic: async (uuid: string): Promise<{message: string}> => {
        const headers = getAuthHeaders('json');
        const response = await fetch(`${API_BASE_URL}/assets/${uuid}/share?targetType=public&targetId=__PUBLIC__`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
        });
        return handleResponse<{message: string}>(response);
    },

    // --- SEARCH OPERATIONS ---

    /**
     * Search users for sharing
     */
    searchUsers: async (query: string): Promise<{users: Array<{id: string, name: string, email: string}>}> => {
        const response = await fetch(`${API_BASE_URL}/assets/search/users?q=${encodeURIComponent(query)}`, {
            credentials: 'include'
        });
        return handleResponse<{users: Array<{id: string, name: string, email: string}>}>(response);
    },

    /**
     * Search organizations for sharing
     */
    searchOrganizations: async (query: string): Promise<{organizations: Array<{id: string, name: string, slug: string}>}> => {
        const response = await fetch(`${API_BASE_URL}/assets/search/organizations?q=${encodeURIComponent(query)}`, {
            credentials: 'include'
        });
        return handleResponse<{organizations: Array<{id: string, name: string, slug: string}>}>(response);
    },
};
